import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import { ILocationAnalysisEvent } from "src/store/locationAnalysisEvent/types";
import { LOCATION_ANALYSIS_EVENT_LABEL } from "src/constants/labels/locationAnalysisEventLabel";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const LOCATION_ANALYSIS_EVENTS_COLUMNS = [
  "type",
  "recordDate",
  "solicitor",
  "scheduled",
  "deliveredAt",
  "created",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type LocationAnalysisEventsColumnData = {
  jobNumber: ColumnType;
  type: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  created: ColumnType;
  scheduled: ColumnType;
  deliveredAt: ColumnType;
};

export function getColumnData({
  getSort,
  onOrderingChange,
}: ColumnDataHelpers): LocationAnalysisEventsColumnData {
  return {
    type: {
      sort: getSort("type"),
      sortFn: (data: unknown) => onOrderingChange(data, "type"),
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    recordDate: {
      sort: getSort("record_date"),
      sortFn: (data: unknown) => onOrderingChange(data, "record_date"),
      style: "text",
      title: "Record Date",
    },
    solicitor: {
      sort: getSort("solicitor__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "solicitor__name"),
      style: "text",
      title: "Solicitor",
    },
    created: {
      sort: getSort("created"),
      sortFn: (data: unknown) => onOrderingChange(data, "created"),
      style: "text",
      title: "Created",
    },
    jobNumber: {
      sort: getSort("job_number"),
      sortFn: (data: unknown) => onOrderingChange(data, "job_number"),
      style: "text",
      title: "Job Number",
    },
    scheduled: {
      sort: getSort("scheduled"),
      sortFn: (data: unknown) => onOrderingChange(data, "scheduled"),
      style: "text",
      title: "Scheduled",
    },
    deliveredAt: {
      sort: getSort("generated_at"),
      sortFn: (data: unknown) => onOrderingChange(data, "generated_at"),
      style: "text",
      title: "Delivered",
    },
  };
}

export function getRowData(
  events: ILocationAnalysisEvent[],
  onRowClick: (event: ILocationAnalysisEvent) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: ILocationAnalysisEvent;
      idx: string;
    };
    onRowClick(event);
  };

  return events.map((event, idx) => {
    const {
      id,
      issuer,
      type,
      recordDate,
      solicitor,
      jobNumber,
      scheduledGenerationAt,
      generatedAt,
      created,
    } = event;

    return {
      id,
      data: { event, idx },
      rowData: {
        issuer: {
          content: issuer.name,
          clickHandler: handleRowClick,
        },
        type: {
          content: LOCATION_ANALYSIS_EVENT_LABEL[type],
          clickHandler: handleRowClick,
        },
        recordDate: {
          content: dayjs(recordDate).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        solicitor: {
          content: solicitor.name,
          clickHandler: handleRowClick,
        },
        created: {
          content: dayjs(created).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        jobNumber: {
          content: jobNumber || "-",
          clickHandler: handleRowClick,
        },
        scheduled: {
          content: scheduledGenerationAt
            ? dayjs(scheduledGenerationAt).format("MM/DD/YYYY")
            : "",
          clickHandler: handleRowClick,
        },
        deliveredAt: {
          content: generatedAt ? dayjs(generatedAt).format("MM/DD/YYYY") : "",
          clickHandler: handleRowClick,
        },
      },
    };
  });
}
